import { useEffect, useState } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { ArrowRight, Key, CheckCircle2, LifeBuoy } from 'lucide-react';
import { isDesktop } from '../lib/platform';
import { useAuth } from '../hooks/useAuth';
import { getTrialDaysRemaining } from '../lib/license';
import { planSummary, isPaidTier } from '../lib/planSummary';
import ActivationModal from '../components/ActivationModal';

const SUPPORT_EMAIL = 'support@scrivar.com';

/**
 * Post-verify Welcome screen (desktop-only). Shown right after a fresh sign-in
 * (Login routes desktop users here). Confirms the account + effective plan and
 * offers an activation-code re-entry, then Continue → /tools.
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const { user, account, loading, refreshAccount } = useAuth();
  const [showActivation, setShowActivation] = useState(false);

  useEffect(() => {
    // Web never sees this — straight to the tools.
    if (!isDesktop()) { navigate('/tools', { replace: true }); return; }
    // Defensive: the hard gate already redirects signed-out users, but if one
    // lands here directly, send them to sign in.
    if (!loading && !user) navigate('/login', { replace: true });
  }, [navigate, loading, user]);

  function handleContinue() {
    if (window.electronAPI?.markFirstRunComplete) {
      window.electronAPI.markFirstRunComplete().catch(() => { /* non-fatal */ });
    }
    navigate('/tools', { replace: true });
  }

  if (!isDesktop() || !user) return null;

  const email = account?.email || user?.email || '';
  const { plan, note } = planSummary(account?.tier, getTrialDaysRemaining());
  // Only offer code entry to users who aren't already licensed. Once a code is
  // registered (or a plan is paid), re-asking for a code is confusing.
  const licensed = isPaidTier(account?.tier);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex flex-col items-center justify-center px-6 py-12">
      <ActivationModal
        open={showActivation}
        onClose={() => setShowActivation(false)}
        onActivated={() => refreshAccount({ forceTokenRefresh: true })}
      />

      <div className="w-full max-w-md text-center">
        <div
          className="w-14 h-14 rounded-2xl mx-auto mb-6 flex items-center justify-center text-white shadow-lg select-none"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
          aria-hidden="true"
        >
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">You&rsquo;re all set</h1>
        <p className="text-sm text-gray-600 mb-6">
          Welcome to Scrivar. You&rsquo;re signed in{email ? <> as <span className="font-medium text-gray-800">{email}</span></> : ''}.
        </p>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 mb-4 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Your plan</p>
          <p className="text-base font-semibold text-gray-900">{plan}</p>
          <p className="text-sm text-gray-500">{note}</p>
        </div>

        {!licensed && (
          <button
            type="button"
            onClick={() => setShowActivation(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 border border-gray-200 bg-white text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50"
          >
            <Key className="w-4 h-4" aria-hidden="true" />
            Have an activation code? Enter it
          </button>
        )}

        <button
          type="button"
          onClick={handleContinue}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-xl text-sm font-semibold hover:bg-brand-dark shadow-sm"
        >
          Continue to Scrivar
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>

        <p className="mt-6 text-xs text-gray-500 flex items-center justify-center gap-1.5">
          <LifeBuoy className="w-3.5 h-3.5" aria-hidden="true" />
          Need help? <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-700 hover:text-gray-900 underline">{SUPPORT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}
